import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import * as Icons from "lucide-react";
import { toast } from "sonner";

export default function Students() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", gender: "", grade: "", role: "student" });
  const { data, isLoading } = useQuery({ queryKey: ["students", q], queryFn: async () => (await api.get(`/users?role=student${q ? `&q=${q}` : ""}`)).data });
  const { data: batches } = useQuery({ queryKey: ["batches"], queryFn: async () => (await api.get("/batches")).data });

  const create = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    try {
      await api.post("/users", { ...form, gender: form.gender || null, batch_id: batches?.[0]?.id, teacher_id: user.role === "teacher" ? user.id : null });
      toast.success("Student created! Default password: champ123");
      setOpen(false); setForm({ name: "", email: "", gender: "", grade: "", role: "student" });
      qc.invalidateQueries({ queryKey: ["students"] });
    } catch (e) { toast.error(apiErr(e)); }
  };

  const del = async (id) => { await api.delete(`/users/${id}`); toast.success("Student removed"); qc.invalidateQueries({ queryKey: ["students"] }); };
  const purgeDemo = async () => {
    const r = await api.post("/admin/purge-demo");
    toast.success(`Removed ${r.data.removed} demo account(s) and their data`);
    qc.invalidateQueries();
  };

  if (isLoading) return <Loader />;

  return (
    <div className="space-y-5" data-testid="students-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-head text-3xl text-csh-blue">Students 👦👧</h2>
        <div className="flex gap-2">
          <div className="relative">
            <Icons.Search size={16} className="absolute left-3 top-3 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="rounded-full border-2 pl-9" data-testid="student-search" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="rounded-full gradient-purple text-white" data-testid="add-student-btn"><Icons.Plus size={18} /> Add</Button></DialogTrigger>            <DialogContent className="rounded-3xl">
              <DialogHeader><DialogTitle className="font-head text-2xl text-csh-blue">New Student</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-2 py-6" data-testid="new-student-name" />
                <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border-2 py-6" data-testid="new-student-email" />
                <div className="grid grid-cols-2 gap-3">
                  <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                    <SelectTrigger className="rounded-xl border-2 py-6" data-testid="new-student-gender"><SelectValue placeholder="Gender" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Boy">Boy</SelectItem>
                      <SelectItem value="Girl">Girl</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input placeholder="Grade" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} className="rounded-xl border-2 py-6" />
                </div>
                <Button onClick={create} data-testid="save-student-btn" className="w-full rounded-full gradient-orange py-6 font-bold text-white">Create Student</Button>
              </div>
            </DialogContent>
          </Dialog>
          {user?.role === "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-full border-2 border-red-200 text-red-600 hover:bg-red-50" data-testid="clear-demo-btn"><Icons.Trash2 size={16} /> Clear Demo Data</Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-3xl">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-head text-2xl text-csh-blue">Clear all demo data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This permanently removes all sample accounts (the demo coach and students ending in <b>@demo.csh</b>) plus their points, badges, submissions, attendance and stories. Your admin account and any real users you created are kept. This cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={purgeDemo} data-testid="confirm-clear-demo" className="rounded-full bg-red-600 hover:bg-red-700">Yes, clear it</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      {(!data || data.length === 0) ? <EmptyState icon="Users" title="No students" text="Add students to get started." /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((s) => (
            <div key={s.id} onClick={() => navigate(`/staff/students/${s.id}`)} data-testid={`student-card-${s.id}`}
              className="flex cursor-pointer items-center gap-3 rounded-3xl bg-white p-4 shadow-sm border border-black/5 card-pop">
              <img src={resolveAvatar(s.avatar)} alt="" className="h-14 w-14 rounded-2xl object-cover" />
              <div className="flex-1 min-w-0">
                <h3 className="font-head text-lg text-csh-blue truncate">{s.name}</h3>
                <p className="text-xs text-muted-foreground">{s.grade}{s.gender ? ` · ${s.gender}` : ""}</p>
                <div className="mt-1 flex gap-3 text-xs font-bold">
                  <span className="text-csh-orange">⭐ {s.points}</span>
                  <span className="text-csh-purple">🏅 {s.badges_count}</span>
                  <span className="text-green-600">✓ {s.approved_quests}</span>
                </div>
              </div>
              <button data-testid={`delete-student-${s.id}`}
                onClick={(e) => { e.stopPropagation(); if (window.confirm(`Remove ${s.name}? This deletes their points, badges and submissions.`)) del(s.id); }}
                className="rounded-lg p-2 hover:bg-red-50"><Icons.Trash2 size={18} className="text-red-500" /></button>
              <Icons.ChevronRight className="text-muted-foreground" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
